import { ScrollReveal } from "@/components/ScrollReveal";
import { useScrollLinked } from "@/hooks/useScrollLinked";
import { ArrowRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/scrollToSection";
import { useLocale } from "@/i18n/LocaleProvider";

const CTASection = () => {
  const { ref, progress } = useScrollLinked(0.9, 0.2);
  const { t } = useTranslation();
  const { localize } = useLocale();
  const navigate = useNavigate();

  return (
    <section className="section-y-wide bg-background overflow-hidden relative">
      {/* Tech grid + dots backdrop */}
      <div className="absolute inset-0 tech-grid opacity-30" />
      <div className="absolute inset-0 tech-dots opacity-[0.15]" />

      {/* Subtle top/bottom fade for depth */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-background via-transparent to-background" />


      {/* Animated SVG rings — scroll-linked rotation */}
      <svg
        className="absolute -top-24 -right-24 w-[500px] h-[500px] opacity-[0.06] text-accent"
        style={{ transform: `rotate(${progress * 90}deg)` }}
        viewBox="0 0 400 400"
      >
        <circle cx="200" cy="200" r="150" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <circle cx="200" cy="200" r="120" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="8 12" />
        <circle cx="200" cy="200" r="90" fill="none" stroke="currentColor" strokeWidth="0.5" />
      </svg>
      <svg
        className="absolute -bottom-20 -left-20 w-[400px] h-[400px] opacity-[0.05] text-primary"
        style={{ transform: `rotate(${-progress * 60}deg)` }}
        viewBox="0 0 400 400"
      >
        <rect x="50" y="50" width="300" height="300" rx="40" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <rect x="90" y="90" width="220" height="220" rx="30" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="6 10" />
      </svg>

      <div className="container relative" ref={ref}>
        <ScrollReveal className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-sm mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="mono-label text-muted-foreground">{t("cta.kicker")}</span>
          </div>

          <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] mb-6 tracking-tight">
            {t("cta.title")} <span className="gradient-text">{t("cta.titleHighlight")}</span>
          </h2>
          <p className="text-[15px] sm:text-lg md:text-xl text-muted-foreground mb-10 max-w-xl mx-auto leading-relaxed">
            {t("cta.body")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
              <a
                href={localize("/contact")}
                onClick={(e) => { e.preventDefault(); navigate(localize("/contact")); }}
                className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-semibold text-lg transition-transform duration-200 active:scale-[0.97] group"

              >
                {t("cta.primary")}
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </span>
            <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
              <a
                href="#blueprint"
                onClick={(e) => { e.preventDefault(); scrollToSection("blueprint"); }}
                className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-card/40 text-foreground font-semibold text-lg transition-transform duration-200 active:scale-[0.97] group"
              >
                <Sparkles size={18} className="text-accent" />
                {t("cta.secondary")}
              </a>
            </span>
          </div>

          <p className="font-mono text-xs tracking-[0.15em] text-muted-foreground/50 mt-8">
            {t("cta.footnote")}
          </p>


        </ScrollReveal>
      </div>
    </section>
  );
};

export default CTASection;
